import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import type { DrawEvent } from 'ol/interaction/Draw';
import Draw from 'ol/interaction/Draw';

import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

export class FreehandLineTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.freehand;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private features: Feature<Geometry>[] = [];

  private readonly drawInteraction: Draw;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Freehand';

    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'LineString',
      freehand: true,
      style: getFeatureStyle,
    });

    this.drawInteraction.on('drawstart', this.onDrawStart.bind(this));
    this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
  }

  override activate(): void {
    super.activate();
    this.drawToolManager.map?.getOlMapOrThrow().addInteraction(this.drawInteraction);
  }

  override deactivate(): void {
    super.deactivate();
    this.drawToolManager.map?.getOlMapOrThrow().removeInteraction(this.drawInteraction);
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    for (const feature of this.features) {
      feature.setProperties(this.getDefaultProperties());
      feature.setStyle(getFeatureStyle);
    }
    if (this.features.length > 0) {
      this.markDirty();
    }
  }

  onDrawStart(event: DrawEvent): void {
    event.feature.setProperties(this.getDefaultProperties());
    event.feature.setStyle(getFeatureStyle);
  }

  onDrawEnd(event: DrawEvent): void {
    event.feature.setProperties(this.getDefaultProperties());
    event.feature.setStyle(getFeatureStyle);
    this.features.push(event.feature);
    this.markDirty();
  }

  // The freehand tool often has multiple features, override and save all of them.
  override async save(): Promise<void> {
    for (const featureToSave of this.features) {
      await super.save(featureToSave);
    }
  }

  override loadFeature(feature: Feature<Geometry>): void {
    super.loadFeature(feature);
    this.features.push(feature);
  }
}
